/**
 * Test if a point is inside a polygon using ray-casting algorithm
 * @param point - The point to test [lon, lat]
 * @param polygon - Array of polygon vertices [lon, lat]
 * @returns true if point is inside polygon
 */
function isPointInPolygon(point: [number, number], polygon: Array<[number, number]>): boolean {
    const [x, y] = point;
    let inside = false;
    
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const [xi, yi] = polygon[i];
        const [xj, yj] = polygon[j];
        
        const intersect = ((yi > y) !== (yj > y))
            && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
        
        if (intersect) {
            inside = !inside;
        }
    }
    
    return inside;
}

/**
 * Calculate the circumradius (center to vertex distance) of a hexagon
 * For flat-top hexagons, circumradius equals the hexSize parameter
 * @param hexSizeLat - Hexagon size in latitude degrees
 * @param hexSizeLon - Hexagon size in longitude degrees
 * @returns Approximate radius for distance pre-filtering
 */
function calculateHexagonRadius(hexSizeLat: number, hexSizeLon: number): number {
    // For a flat-top hexagon, the circumradius is the hexSize
    // Use average of lat/lon sizes for approximate circular distance check
    return Math.sqrt(hexSizeLat * hexSizeLat + hexSizeLon * hexSizeLon);
}

/**
 * Generate hexagon grid for GPS coordinates
 * Creates a tessellated grid of flat-top hexagons covering the specified bounds
 * @param bounds - Geographic bounds for the grid
 * @param hexSizeMeters - Size of each hexagon in meters (circumradius)
 * @returns Array of hexagons with center coordinates and vertex arrays
 */
export function generateHexagonGrid(
    bounds: { minLat: number; maxLat: number; minLon: number; maxLon: number },
    hexSizeMeters: number = 20
) {
    const hexagons: Array<{
        center: [number, number];
        coordinates: Array<[number, number]>;
    }> = [];

    // Convert hexagon size from meters to degrees (approximate)
    const latPerMeter = 1 / 111000;
    const hexSizeLat = hexSizeMeters * latPerMeter;
    const avgLat = (bounds.minLat + bounds.maxLat) / 2;
    const lonPerMeter = 1 / (111000 * Math.cos((avgLat * Math.PI) / 180));
    const hexSizeLon = hexSizeMeters * lonPerMeter;

    // Proper hexagon tessellation
    const hexWidth = hexSizeLon * Math.sqrt(3);
    const hexHeight = hexSizeLat * 2;
    
    // Vertical spacing: 3/4 of hexagon height for proper tessellation
    const verticalSpacing = hexHeight * 0.75;
    // Horizontal spacing: full hexagon width
    const horizontalSpacing = hexWidth;

    let row = 0;
    for (let lat = bounds.minLat; lat <= bounds.maxLat; lat += verticalSpacing) {
        // Offset every other row by half hexagon width for tessellation
        const offset = (row % 2) * (hexWidth / 2);
        
        for (let lon = bounds.minLon - offset; lon <= bounds.maxLon; lon += horizontalSpacing) {
            // Generate hexagon vertices (flat-top orientation)
            // Start at 30 degrees and increment by 60 for flat-top hexagons
            const coords: Array<[number, number]> = [];
            for (let i = 0; i < 6; i++) {
                const angleDeg = 30 + (60 * i); // Offset by 30 degrees for flat-top
                const angleRad = (Math.PI / 180) * angleDeg;
                const vertexLon = lon + hexSizeLon * Math.cos(angleRad);
                const vertexLat = lat + hexSizeLat * Math.sin(angleRad);
                coords.push([vertexLon, vertexLat]);
            }
            coords.push(coords[0]); // Close polygon

            hexagons.push({
                center: [lon, lat],
                coordinates: coords
            });
        }
        row++;
    }

    return hexagons;
}

/**
 * Bin GPS points into hexagons and calculate average values
 * Uses two-stage filtering: distance pre-filter + accurate point-in-polygon test
 * Applies IDW interpolation to fill empty hexagons with interpolated values
 * @param hexagons - Array of hexagons from generateHexagonGrid
 * @param points - Array of GPS points with values to bin
 * @param areaPolygon - Optional area polygon to filter hexagons (only keep hexagons inside area)
 * @param power - IDW power parameter (default 2)
 * @returns Array of all hexagons with measured or interpolated values
 */
export function binPointsToHexagons(
    hexagons: Array<{ center: [number, number]; coordinates: Array<[number, number]> }>,
    points: Array<{ lat: number; lon: number; value: number }>,
    areaPolygon?: Array<[number, number]>,
    power: number = 2
) {
    if (points.length === 0) {
        return [];
    }

    // Calculate approximate hexagon radius for distance pre-filtering
    // Use first hexagon to determine radius (all hexagons should be same size)
    let preFilterRadius = 0.0003; // Fallback default
    if (hexagons.length > 0) {
        const firstHex = hexagons[0];
        const [centerLon, centerLat] = firstHex.center;
        const [vertex0Lon, vertex0Lat] = firstHex.coordinates[0];
        preFilterRadius = Math.sqrt(
            Math.pow(vertex0Lon - centerLon, 2) + 
            Math.pow(vertex0Lat - centerLat, 2)
        );
        // Add 10% margin to ensure we don't miss edge points
        preFilterRadius *= 1.1;
    }

    // Stage 1: Bin actual measurements into hexagons
    const hexagonsWithData: Array<{
        coordinates: Array<[number, number]>;
        center: [number, number];
        value: number;
        count: number;
        hasData: boolean;
    }> = [];

    hexagons.forEach(hex => {
        // Fast distance pre-filter
        const nearbyPoints = points.filter(p => {
            const dist = Math.sqrt(
                Math.pow(p.lon - hex.center[0], 2) +
                Math.pow(p.lat - hex.center[1], 2)
            );
            return dist <= preFilterRadius;
        });

        // Accurate point-in-polygon test for nearby points
        const pointsInHex = nearbyPoints.filter(p => {
            return isPointInPolygon([p.lon, p.lat], hex.coordinates);
        });

        if (pointsInHex.length > 0) {
            const avgValue = pointsInHex.reduce((sum, p) => sum + p.value, 0) / pointsInHex.length;
            hexagonsWithData.push({
                coordinates: hex.coordinates,
                center: hex.center,
                value: avgValue,
                count: pointsInHex.length,
                hasData: true
            });
        } else {
            // Keep hexagon but mark as needing interpolation
            hexagonsWithData.push({
                coordinates: hex.coordinates,
                center: hex.center,
                value: 0, // Placeholder
                count: 0,
                hasData: false
            });
        }
    });

    // Stage 2: Apply IDW interpolation to empty hexagons
    const filledHexagons = hexagonsWithData.filter(h => h.hasData);
    
    if (filledHexagons.length === 0) {
        return [];
    }

    const allHexagonsWithValues = hexagonsWithData.map(hex => {
        if (hex.hasData) {
            // Already has measured data
            return {
                coordinates: hex.coordinates,
                center: hex.center,
                value: hex.value,
                count: hex.count
            };
        } else {
            // Interpolate using IDW from hexagons with data
            let numerator = 0;
            let denominator = 0;

            filledHexagons.forEach(filledHex => {
                const dx = hex.center[0] - filledHex.center[0];
                const dy = hex.center[1] - filledHex.center[1];
                const distance = Math.sqrt(dx * dx + dy * dy);
                
                if (distance === 0) {
                    // Should not happen, but handle it
                    numerator = filledHex.value;
                    denominator = 1;
                } else {
                    const weight = 1 / Math.pow(distance, power);
                    numerator += weight * filledHex.value;
                    denominator += weight;
                }
            });

            const interpolatedValue = denominator > 0 ? numerator / denominator : 0;

            return {
                coordinates: hex.coordinates,
                center: hex.center,
                value: interpolatedValue,
                count: 0 // Mark as interpolated
            };
        }
    });

    // Filter hexagons to only include those inside the area polygon (if provided)
    if (areaPolygon && areaPolygon.length > 0) {
        return allHexagonsWithValues
            .filter(hex => isPointInPolygon(hex.center, areaPolygon))
            .map(({ coordinates, value, count }) => ({ coordinates, value, count }));
    }

    // Return all hexagons if no area polygon filter
    return allHexagonsWithValues.map(({ coordinates, value, count }) => ({ coordinates, value, count }));
}
